import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as Effect from 'effect/Effect';
import { pipe } from 'effect/Function';
import { CategoryService } from '@/effects/categories/category.service';
import { MainLayer } from '@/effects/layers';
import { runEffect } from '@/effects/runtime';
import { QUERY_KEYS } from '@/lib/constants';

interface CreateCategoryParams {
  groupId: string;
  name: string;
  description?: string;
  emoji?: string;
  default_splits?: Array<{
    user_id: string;
    weight: number;
  }>;
}

interface UpdateCategoryParams {
  categoryId: string;
  name: string;
  description?: string;
  emoji?: string;
  default_splits?: Array<{
    user_id: string;
    weight: number;
  }>;
}

export function useCreateCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['category', 'create'],
    mutationFn: async (params: CreateCategoryParams) => {
      const program = pipe(
        CategoryService,
        Effect.flatMap((service) => service.create(params)),
        Effect.provide(MainLayer)
      );

      return runEffect(program);
    },
    onSuccess: (_, params) => {
      // Invalidate group categories to refresh the list
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GROUP_CATEGORIES, params.groupId],
      });
      // Also invalidate user groups to refresh pinned categories
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.USER_GROUPS],
      });
    },
  });
}

export function useUpdateCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['category', 'update'],
    mutationFn: async (params: UpdateCategoryParams) => {
      const program = pipe(
        CategoryService,
        Effect.flatMap((service) => service.update(params)),
        Effect.provide(MainLayer)
      );

      return runEffect(program);
    },
    onSuccess: (_data, params) => {
      // Invalidate all group categories (since we don't know the group from params)
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GROUP_CATEGORIES],
      });
      // Invalidate specific category data
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.CATEGORY_SUMMARY, params.categoryId],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.CATEGORY_SPLITS, params.categoryId],
      });
    },
  });
}

export function useDeleteCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['category', 'delete'],
    mutationFn: async (categoryId: string) => {
      const program = pipe(
        CategoryService,
        Effect.flatMap((service) => service.delete(categoryId)),
        Effect.provide(MainLayer)
      );

      return runEffect(program);
    },
    onSuccess: (_data, _categoryId) => {
      // Invalidate all group categories
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GROUP_CATEGORIES],
      });
      // Invalidate user groups to refresh if it was pinned
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.USER_GROUPS],
      });
    },
  });
}